import { LoadCapacity } from "./LoadCapacity";
import {LiftingEquipment} from "./LiftingEquipment";

export interface CachedInspectionResult {
  id: string;
  inspection_at: string;
  score: number;
  compliance_percentage_per_category: { [category: string]: number };
  critical_findings_total: number;
  critical_findings_fixed: number;
  critical_findings_open: number;
  critical_finding_question_ids: string[];
  lsr_findings_total: number;
  lsr_findings_fixed: number;
  lsr_findings_open: number;
  lsr_finding_question_ids: string[];
}

export type InspectionReportStatus = "draft" | "definitive";

export const inspectionStatusLabels: Record<InspectionReportStatus, string> = {
  draft: "Voorlopig",
  definitive: "Definitief",
};

export class Inspection {
  public readonly status: "Concept" | "In uitvoering" | "Afgerond" | "Gearchiveerd";
  public readonly lastUpdated: Date;
  constructor(
    public readonly id: string,
    public readonly companyId: string,
    public readonly projectId: string,
    public readonly name: string,
    public readonly questionSetId: string,
    public readonly assessorId: string | null,
    public readonly contactName: string | null,
    public readonly contactEmail: string | null,
    public readonly contactPhone: string | null,
    public readonly contactExtra: string | null,
    public readonly inspectionAt: Date | null,
    public readonly startedAt: Date | null,
    public readonly finishedAt: Date | null,
    public readonly inspectionStatus: InspectionReportStatus | null,
    public readonly lastEditorId: string | null,
    public readonly inspectionNumber: string | null,
    public readonly inspectionLocation: string | null,
    public readonly loadCapacities: LoadCapacity[],
    public readonly loadCapacitiesFormatted: string[],
    public readonly liftingEquipments: LiftingEquipment[],
    public readonly liftingEquipmentsFormatted: string[],
    public readonly extraFields: { [key: string]: string },
    public readonly extraFieldsFormatted: { [key: string]: string },
    public readonly cachedResults: CachedInspectionResult | null,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
    public readonly archivedAt: Date | null,
  ) {
    if (this.archivedAt !== null) {
      this.status = "Gearchiveerd";
    } else if (this.finishedAt !== null) {
      this.status = "Afgerond";
    } else if (this.startedAt === null) {
      this.status = "Concept";
    } else {
      this.status = "In uitvoering";
    }

    this.lastUpdated = new Date(
      Math.max(
        this.createdAt.getTime(),
        this.updatedAt.getTime(),
        startedAt?.getTime() ?? 0,
        finishedAt?.getTime() ?? 0,
      ),
    );
  }

  static fromJson(json: any): Inspection {
    return new Inspection(
      json["id"],
      json["company_id"],
      json["project_id"],
      json["name"],
      json["question_set_id"],
      json["assessor_id"],
      json["contact_name"],
      json["contact_email"],
      json["contact_phone"],
      json["contact_extra"],
      json["inspection_at"] ? new Date(json["inspection_at"]) : null,
      json["started_at"] ? new Date(json["started_at"]) : null,
      json["finished_at"] ? new Date(json["finished_at"]) : null,
      json["status"] ? json["status"] : null,
      json["latest_editor_id"],
      json["inspection_number"],
      json["inspection_location"],
      json["load_capacities"]
        ? json["load_capacities"].map((cap: any) => LoadCapacity.fromJson(cap))
        : [],
      json["load_capacities_formatted"] ?? {},
      json["lifting_equipments"]
        ? json["lifting_equipments"].map((le: any) => LiftingEquipment.fromJson(le))
        : [],
      json["lifting_equipments_formatted"] ?? {},
      json["extra_fields"] ?? {},
      json["extra_fields_formatted"] ?? {},
      json["cached_results"],
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
      json["archived_at"] ? new Date(json["archived_at"]) : null,
    );
  }
}
